'use client';

import type * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import {
	Controller,
	type ControllerProps,
	type FieldPath,
	type FieldValues,
	FormProvider,
	useFormContext,
} from 'react-hook-form';

import { Label } from '../../atoms/label/label';
import { cn } from '../../lib/cn';

/** Radice del form: fornisce il contesto react-hook-form a {@link FormField} e agli altri componenti Form*. Alias di `FormProvider`. */
const Form = FormProvider;

type FormFieldContextValue<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
	name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue | null>(null);

/** Collega un campo controllato di react-hook-form (props identiche a `Controller`) al contesto letto da {@link FormLabel}/{@link FormControl}/{@link FormDescription}/{@link FormMessage}. */
const FormField = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	...props
}: ControllerProps<TFieldValues, TName>) => {
	return (
		<FormFieldContext.Provider value={{ name: props.name }}>
			<Controller {...props} />
		</FormFieldContext.Provider>
	);
};

const useFormField = () => {
	const fieldContext = React.useContext(FormFieldContext);
	const itemContext = React.useContext(FormItemContext);
	const { getFieldState, formState } = useFormContext();

	if (!fieldContext) {
		throw new Error('useFormField should be used within <FormField>');
	}

	if (!itemContext) {
		throw new Error('useFormField should be used within <FormItem>');
	}

	const fieldState = getFieldState(fieldContext.name, formState);

	const { id } = itemContext;

	return {
		id,
		name: fieldContext.name,
		formItemId: `${id}-form-item`,
		formDescriptionId: `${id}-form-item-description`,
		formMessageId: `${id}-form-item-message`,
		...fieldState,
	};
};

type FormItemContextValue = {
	id: string;
};

const FormItemContext = React.createContext<FormItemContextValue | null>(null);

/** Props di {@link FormItem}: nessuna prop custom, solo gli attributi HTML standard di un div. */
export interface FormItemProps extends React.HTMLAttributes<HTMLDivElement> {}

/** Contenitore di un singolo campo form: genera l'id condiviso da {@link FormLabel}, {@link FormControl}, {@link FormDescription} e {@link FormMessage}. */
const FormItem = React.forwardRef<HTMLDivElement, FormItemProps>(({ className, ...props }, ref) => {
	const id = React.useId();

	return (
		<FormItemContext.Provider value={{ id }}>
			<div ref={ref} className={cn('space-y-2', className)} {...props} />
		</FormItemContext.Provider>
	);
});
FormItem.displayName = 'FormItem';

/** Props di {@link FormLabel}: nessuna prop custom, solo gli attributi HTML standard di `<label>`. */
export interface FormLabelProps extends React.ComponentPropsWithoutRef<
	typeof LabelPrimitive.Root
> {}

/** {@link Label} collegata automaticamente al campo del {@link FormItem} corrente; si colora di rosso se il campo ha un errore di validazione. */
const FormLabel = React.forwardRef<React.ElementRef<typeof LabelPrimitive.Root>, FormLabelProps>(
	({ className, ...props }, ref) => {
		const { error, formItemId } = useFormField();

		return (
			<Label
				ref={ref}
				className={cn(error && 'text-destructive', className)}
				htmlFor={formItemId}
				{...props}
			/>
		);
	},
);
FormLabel.displayName = 'FormLabel';

/** Props di {@link FormControl}: nessuna prop custom, inoltra tutto al suo unico figlio (Radix Slot). */
export interface FormControlProps extends React.ComponentPropsWithoutRef<typeof Slot> {}

/** Collega il controllo effettivo del campo (input, select, ecc.) allo stato/errori di react-hook-form via `aria-*`, senza aggiungere un wrapper DOM (Radix Slot). */
const FormControl = React.forwardRef<React.ElementRef<typeof Slot>, FormControlProps>(
	({ ...props }, ref) => {
		const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

		return (
			<Slot
				ref={ref}
				id={formItemId}
				aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
				aria-invalid={!!error}
				{...props}
			/>
		);
	},
);
FormControl.displayName = 'FormControl';

/** Props di {@link FormDescription}: nessuna prop custom, solo gli attributi HTML standard di un paragrafo. */
export interface FormDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {}

/** Testo di aiuto sotto il campo, collegato per l'accessibilità tramite `aria-describedby`. */
const FormDescription = React.forwardRef<HTMLParagraphElement, FormDescriptionProps>(
	({ className, ...props }, ref) => {
		const { formDescriptionId } = useFormField();

		return (
			<p
				ref={ref}
				id={formDescriptionId}
				className={cn('text-sm text-muted-foreground', className)}
				{...props}
			/>
		);
	},
);
FormDescription.displayName = 'FormDescription';

/** Props di {@link FormMessage}: `children` è usato solo come fallback quando il campo non ha errori. */
export interface FormMessageProps extends React.HTMLAttributes<HTMLParagraphElement> {}

/** Messaggio di errore di validazione del campo (mostra `error.message` se presente, altrimenti `children`, altrimenti nulla). */
const FormMessage = React.forwardRef<HTMLParagraphElement, FormMessageProps>(
	({ className, children, ...props }, ref) => {
		const { error, formMessageId } = useFormField();
		const body = error ? String(error?.message ?? '') : children;

		if (!body) {
			return null;
		}

		return (
			<p
				ref={ref}
				id={formMessageId}
				className={cn('text-sm font-medium text-destructive', className)}
				{...props}
			>
				{body}
			</p>
		);
	},
);
FormMessage.displayName = 'FormMessage';

export {
	useFormField,
	Form,
	FormItem,
	FormLabel,
	FormControl,
	FormDescription,
	FormMessage,
	FormField,
};
